import React, { Suspense, lazy } from 'react';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from './lib/api.js';
import { initBookingAttribution } from './lib/attribution.js';
import { setAnalyticsMeasurementId } from './lib/tracker.js';
import { initClarity, setClarityProjectId } from './lib/clarity.js';
import { setTenantFormattingDefaults } from './lib/formatting.js';
import { setTenantTimePreferences } from './lib/time.js';
import Skeleton from './components/Skeleton.jsx';
import MaintenanceBanner from './components/MaintenanceBanner.jsx';
import UpdateBanner from './components/UpdateBanner.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';

const BookingPage = lazy(() => import('./pages/BookingPage.jsx'));
const HomePage = lazy(() => import('./pages/HomePage.jsx'));
const OffsitePage = lazy(() => import('./pages/OffsitePage.jsx'));
const ConfirmationPage = lazy(() => import('./pages/ConfirmationPage.jsx'));
const CancelledPage = lazy(() => import('./pages/CancelledPage.jsx'));
const PortalPage = lazy(() => import('./pages/PortalPage.jsx'));
const TermsAndConditionsPage = lazy(() => import('./pages/TermsAndConditionsPage.jsx'));

const RouteFallback = () => (
  <main className="page">
    <section className="card card--soft stack" aria-busy="true">
      <Skeleton width={220} height={30} />
      <Skeleton width="68%" height={14} />
      <Skeleton width="52%" height={14} />
    </section>
  </main>
);

const BookingRouteFallback = () => (
  <main className="page page--booking" aria-busy="true" aria-label="Loading booking page">
    <header className="hero hero--fun">
      <div className="hero-brand">
        <Skeleton width={36} height={36} />
        <Skeleton width={260} height={14} />
      </div>
      <Skeleton width="58%" height={52} />
      <Skeleton width="72%" height={18} />
    </header>
    <div className="layout">
      <section className="panel card card--soft stack stack--lg">
        <div className="booking-progress">
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} width={112} height={40} />
          ))}
        </div>
        <section className="step-card">
          <Skeleton width="62%" height={26} />
          <Skeleton width="48%" height={16} />
          <div className="packages-grid">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="package-option skeleton-package">
                <Skeleton width="100%" height={130} />
                <Skeleton width="70%" height={18} />
                <Skeleton width="42%" height={16} />
              </div>
            ))}
          </div>
        </section>
      </section>
      <aside className="summary">
        <section className="card summary-card stack">
          <Skeleton width={160} height={22} />
          <Skeleton width="100%" height={160} />
        </section>
      </aside>
    </div>
  </main>
);

const BookingsUnavailablePage = ({ brandSettings }) => (
  <main className="page page--booking">
    <header className="hero">
      <div className="hero-brand">
        {brandSettings?.logoUrl ? (
          <img
            className="brand-logo"
            src={brandSettings.logoUrl}
            alt={brandSettings.businessName || 'Brand logo'}
          />
        ) : null}
        <p className="eyebrow">{brandSettings?.businessName || 'Build-A-Bear Workshop'}</p>
      </div>
      <h1>Bookings are temporarily unavailable</h1>
      <p className="subtitle">
        {brandSettings?.bookingsModeMessage ||
          'Online booking pages are hidden while bookings are blocked by an administrator.'}
      </p>
    </header>

    <section className="card card--soft stack">
      <p className="muted">Please return to the store locator and try again later.</p>
      <div>
        <a className="btn btn-primary" href="/">
          Return to store locator
        </a>
      </div>
    </section>
  </main>
);

const VERSION_CACHE_KEY = 'bab:last-seen-server-version';

const buildVersionSignature = (data) => {
  if (!data) return '';
  return [data.service, data.version, data.gitSha].filter(Boolean).join('@');
};

export default function App() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [updateDismissed, setUpdateDismissed] = useState(false);

  useEffect(() => {
    initBookingAttribution();
  }, []);

  const pathname = window.location.pathname;
  const isConfirmation = pathname.startsWith('/confirmation/');
  const isCancelled = pathname.startsWith('/checkout/cancelled');
  const isTermsAndConditions =
    pathname === '/terms-and-conditions' || pathname === '/terms-and-conditions/';
  const brandSettingsQuery = useQuery({
    queryKey: ['public-settings'],
    queryFn: () => apiRequest({ path: '/api/public/settings' }),
    retry: false,
  });
  const versionQuery = useQuery({
    queryKey: ['server-version'],
    queryFn: () => apiRequest({ path: '/api/version' }),
    retry: false,
    refetchInterval: 5 * 60 * 1000,
  });
  const brandSettings = brandSettingsQuery.data || null;
  const settingsLoaded = brandSettingsQuery.isSuccess;
  const bookingsAreBlocked = brandSettings ? !brandSettings.bookingsModeEnabled : false;
  const isBookingRoute = pathname.startsWith('/store/') || pathname.startsWith('/offsite/');

  useEffect(() => {
    const signature = buildVersionSignature(versionQuery.data);
    if (!signature) return;

    const previousSignature = window.localStorage.getItem(VERSION_CACHE_KEY);
    if (previousSignature && previousSignature !== signature) {
      setUpdateAvailable(true);
      setUpdateDismissed(false);
    }
    window.localStorage.setItem(VERSION_CACHE_KEY, signature);
  }, [versionQuery.data]);

  useEffect(() => {
    if (!brandSettings) return;
    const root = document.documentElement;
    setTenantFormattingDefaults({
      locale: brandSettings.locale,
      currency: brandSettings.currencyCode,
    });
    setTenantTimePreferences({
      baseTimezone: brandSettings.baseTimezone,
      locale: brandSettings.locale,
    });
    setAnalyticsMeasurementId(brandSettings.googleAnalyticsMeasurementId);
    setClarityProjectId(brandSettings.microsoftClarityId);
    initClarity();
    if (brandSettings.primaryColor) {
      root.style.setProperty('--accent', brandSettings.primaryColor);
    }
    if (brandSettings.secondaryColor) {
      root.style.setProperty('--accent-2', brandSettings.secondaryColor);
    }
  }, [brandSettings]);
  let route = <NotFoundPage />;

  if (isCancelled) {
    route = <CancelledPage brandSettings={brandSettings} />;
  } else if (isTermsAndConditions) {
    route = <TermsAndConditionsPage brandSettings={brandSettings} />;
  } else if (isConfirmation) {
    const bookingRef = pathname.replace('/confirmation/', '');
    route = !bookingRef ? <NotFoundPage /> : <ConfirmationPage bookingRef={bookingRef} brandSettings={brandSettings} />;
  } else if (isBookingRoute && brandSettingsQuery.isLoading) {
    route = <BookingRouteFallback />;
  } else if (isBookingRoute && bookingsAreBlocked) {
    route = <BookingsUnavailablePage brandSettings={brandSettings} />;
  } else if (pathname.startsWith('/offsite/')) {
    const slug = pathname.replace('/offsite/', '');
    route = !slug ? <NotFoundPage /> : <OffsitePage slug={slug} brandSettings={brandSettings} />;
  } else if (pathname.startsWith('/portal')) {
    route = <PortalPage brandSettings={brandSettings} />;
  } else if (pathname.startsWith('/store/')) {
    const slug = pathname.replace('/store/', '');
    route = !slug ? (
      <NotFoundPage />
    ) : (
      <BookingPage brandSettings={brandSettings} initialStoreSlug={slug} />
    );
  } else if (pathname === '/') {
    route = <HomePage brandSettings={brandSettings} />;
  }

  return (
    <>
      {settingsLoaded && bookingsAreBlocked ? (
        <MaintenanceBanner message={brandSettings?.bookingsModeMessage} />
      ) : null}
      {updateAvailable && !updateDismissed ? (
        <UpdateBanner onDismiss={() => setUpdateDismissed(true)} />
      ) : null}
      <Suspense fallback={isBookingRoute ? <BookingRouteFallback /> : <RouteFallback />}>
        {route}
      </Suspense>
    </>
  );
}
